import { useState, useEffect, useRef } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Folder, FolderOpen, Check, Plus, Loader2, X, Save } from "lucide-react";
import { cn } from "@/lib/utils";

interface MoveFolderPopoverProps {
  patientId: string;
  currentFolder: string | null;
  onSelectFolder: (folder: string | null) => void;
  trigger: React.ReactNode;
}

export function MoveFolderPopover({
  patientId,
  currentFolder,
  onSelectFolder,
  trigger,
}: MoveFolderPopoverProps) {
  const [folders, setFolders] = useState<Array<{ name: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showCreateInput, setShowCreateInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open, patientId]);

  useEffect(() => {
    if (showCreateInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCreateInput]);

  async function loadFolders() {
    try {
      setLoading(true);
      // Buscar todas as pastas da tabela de pastas
      // @ts-ignore - patient_gallery_folders table not yet in types
      const { data: foldersData, error: foldersError } = await supabase
        .from("patient_gallery_folders")
        .select("name")
        .eq("patient_id", patientId)
        .order("name", { ascending: true });

      if (foldersError) throw foldersError;

      // Buscar todas as imagens para contar por pasta
      // @ts-ignore - patient_gallery table not yet in types
      const { data: imagesData, error: imagesError } = await supabase
        .from("patient_gallery")
        .select("folder")
        .eq("patient_id", patientId);

      if (imagesError) throw imagesError;

      // Contar imagens por pasta
      const folderCounts: Record<string, number> = {};
      (imagesData || []).forEach((item) => {
        const folder = item.folder || null;
        if (folder) {
          folderCounts[folder] = (folderCounts[folder] || 0) + 1;
        }
      });

      // Criar array com todas as pastas (mesmo as vazias) e suas contagens
      const foldersArray = (foldersData || []).map((folder) => ({
        name: folder.name,
        count: folderCounts[folder.name] || 0,
      }));

      setFolders(foldersArray);
    } catch (error) {
      console.error("Erro ao carregar pastas:", error);
    } finally {
      setLoading(false);
    }
  }

  async function createFolder() {
    if (!newFolderName.trim()) {
      toast.error("Digite um nome para a pasta");
      return;
    }

    const folderName = newFolderName.trim();

    // Verificar se já existe
    if (folders.some(f => f.name === folderName)) {
      toast.error("Já existe uma pasta com este nome");
      return;
    }

    try {
      setIsCreatingFolder(true);

      // Obter usuário autenticado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Usuário não autenticado");
        return;
      }

      // Criar pasta no banco de dados
      const { error } = await supabase
        .from("patient_gallery_folders")
        .insert({
          patient_id: patientId,
          owner_id: user.id,
          name: folderName,
        });

      if (error) {
        // Se for erro de duplicata (UNIQUE constraint)
        if (error.code === "23505") {
          toast.error("Já existe uma pasta com este nome");
        } else {
          throw error;
        }
        return;
      }

      // Recarregar pastas
      await loadFolders();
      
      // Selecionar a nova pasta automaticamente
      onSelectFolder(folderName);
      setNewFolderName("");
      setShowCreateInput(false);
      setOpen(false);
      
      toast.success("Pasta criada e selecionada!");
    } catch (error) {
      console.error("Erro ao criar pasta:", error);
      toast.error("Erro ao criar pasta");
    } finally {
      setIsCreatingFolder(false);
    }
  }

  function handleSelectFolder(folder: string | null) {
    onSelectFolder(folder);
    setOpen(false);
  }

  function handleCancelCreate() {
    setNewFolderName("");
    setShowCreateInput(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2">
          <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
            Mover para pasta
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-1">
              {/* Opção "Sem pasta" */}
              <button
                onClick={() => handleSelectFolder(null)}
                className={cn(
                  "w-full flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors hover:bg-accent",
                  currentFolder === null && "bg-accent"
                )}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4" />
                  <span>Sem pasta</span>
                </div>
                {currentFolder === null && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </button>

              {/* Lista de pastas */}
              {folders.length > 0 && (
                <>
                  {folders.map((folder) => (
                    <button
                      key={folder.name}
                      onClick={() => handleSelectFolder(folder.name)}
                      className={cn(
                        "w-full flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors hover:bg-accent",
                        currentFolder === folder.name && "bg-accent"
                      )}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Folder className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{folder.name}</span>
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {folder.count}
                        </Badge>
                      </div>
                      {currentFolder === folder.name && (
                        <Check className="h-4 w-4 text-primary ml-2 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </>
              )}

              {/* Input para criar nova pasta */}
              {showCreateInput ? (
                <div className="px-2 py-2 space-y-2 border-t mt-1">
                  <div className="flex items-center gap-2">
                    <Input
                      ref={inputRef}
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          createFolder();
                        } else if (e.key === "Escape") {
                          handleCancelCreate();
                        }
                      }}
                      placeholder="Nome da nova pasta"
                      className="h-8 text-sm"
                      disabled={isCreatingFolder}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={handleCancelCreate}
                      disabled={isCreatingFolder}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={createFolder}
                      disabled={isCreatingFolder || !newFolderName.trim()}
                    >
                      {isCreatingFolder ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateInput(true)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors hover:bg-accent text-muted-foreground border-t mt-1 pt-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Adicionar pasta</span>
                </button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

